import { Form, xxlBumps } from '@wrisk/ui-components'
import { isNil } from 'lodash-es'
import { DateTime } from 'luxon'
import React, { FunctionComponent, useCallback, useState } from 'react'
import { useNavigate } from 'react-router'

import { isCancellationReasonInvalid } from '../../../../domain'
import {
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { CancellationCoolingOffConfig } from '../../../../state/configuration'
import { CancellationReasonInput } from '../../../organisms/cancellation'
import { ActionBar } from '../../../organisms/form'
import { FullPage } from '../../../templates'
import { usePolicy } from '../PolicyProvider'
import { CancellationPath } from './CancellationPath'

export interface CancellationCoolingOffPageProps {
  config: CancellationCoolingOffConfig
}

const tKey = TKey('pages.cancel')

export const CancellationCoolingOffPage: FunctionComponent<
  CancellationCoolingOffPageProps
> = ({ config }) => {
  const navigate = useNavigate()

  const { t } = useWriskTranslation()

  const { policy } = usePolicy()

  const [cancellationReason, onReasonChange] = useState<string | null>(null)

  const onDismiss = useCallback(() => navigate('..'), [navigate])

  const onSubmit = useCallback(() => {
    navigate(CancellationPath.CONFIRM, {
      state: {
        cancellationReason,
        dateTime: DateTime.fromISO(policy.policyDetail.inceptedAt).toISO(),
      },
    })
  }, [cancellationReason, navigate, policy])

  return (
    <FullPage
      pageId='policy-cancellation-page'
      header={t(tKey('header'))}
      subheader={t(tKey('subheader'))}
      helpLinkText={t(tKey('help-link-message'), { defaultValue: null })}
      helpLinkUrl={t<string>('links.cancellationTerms', { defaultValue: null })}
    >
      <Form data-testid='policy-cancellation-form' onSubmit={onSubmit}>
        {!isNil(config.reasons) && (
          <CancellationReasonInput
            onChange={onReasonChange}
            reasons={config.reasons}
            value={cancellationReason}
            mb={xxlBumps}
          />
        )}

        <ActionBar
          tKey={tKey}
          destructive
          disabled={isCancellationReasonInvalid(config.reasons, cancellationReason)}
          onDismiss={onDismiss}
        />
      </Form>
    </FullPage>
  )
}
